import React, { useState, useEffect } from 'react';
import { FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';

const API_URL = '/api';

const initialFields = {
  Username: '',
  FullName: '',
  Email: '',
  Phone: '',
  Role: 'Vendoruser',
  IsActive: 1,
  Password: '',
};

const UserFormModal = ({ open, onClose, onSubmit, initialData, isEdit, loading }) => {
  const [fields, setFields] = useState(initialFields);
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        // Map database role to frontend role for editing
        const mappedData = { ...initialData };
        if (initialData.Role) {
          switch (initialData.Role) {
            case 'Superadmin':
              mappedData.Role = 'Superadmin';
              break;
            case 'Vendoradmin':
              mappedData.Role = 'Vendoradmin';
              break;
            case 'Vendoruser':
              mappedData.Role = 'Vendoruser';
              break;
            
            default:
              mappedData.Role = initialData.Role;
          }
        }
        setFields({ ...initialFields, ...mappedData, Password: '' });
      } else {
        setFields(initialFields);
      }
      setErrors({});
      setIsSubmitting(false);
    }
  }, [open, initialData, isEdit]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFields(prev => ({ ...prev, [name]: value }));
  };

  // inside validate()
const validate = () => {
  const errs = {};
  if (!fields.Username) errs.Username = 'Required';
  if (!fields.FullName) errs.FullName = 'Required';
  if (!fields.Email) errs.Email = 'Required';
  if (!fields.Role) errs.Role = 'Required';
  if (!isEdit && !fields.Password) errs.Password = 'Required'; // required only on create
  setErrors(errs);
  return Object.keys(errs).length === 0;
};

// inside handleSubmit
const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;

  const formData = { ...fields };

  // If editing and password is blank, don't include it
  if (isEdit && !formData.Password) {
    delete formData.Password;
  }

  try {
    let response;
    if (isEdit && initialData) {
      response = await fetch(`${API_URL}/user/${initialData.UserID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
    } else {
      response = await fetch(`${API_URL}/user`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
    }

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `Failed to ${isEdit ? 'update' : 'create'} user`);
    }

    onSubmit(formData, isEdit);
  } catch (error) {
    console.error('Error saving user:', error);
    alert(error.message || 'An error occurred while saving the user');
  }
};

  if (!open) return null;

  return (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-40">
    <div className="bg-[#E5E1DA] rounded-4xl shadow-4xl w-full max-w-md md:max-w-4xl p-4 md:p-6 relative overflow-y-auto max-h-[90vh]">
      <button className="absolute top-3 right-3 text-[#2b6777] hover:text-[#52ab98]" onClick={onClose}>
        <FaTimes size={18} />
      </button>
      <h2 className="text-xl md:text-2xl font-bold text-[#2b6777] mb-4 md:mb-6 text-center">
        {isEdit ? 'Edit User' : 'Create New User'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
        {/* User Information */}
        <div className='bg-white p-3 md:p-4 rounded-xl shadow-md'>
          <h3 className="text-base md:text-lg font-bold text-[#2b6777] mb-2">User Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <div className='flex flex-col md:flex-row gap-2 md:justify-between md:items-center gap-x-2'>
              <label className="block md:w-1/4 text-sm font-medium text-[#2b6777] whitespace-nowrap">Username *</label>
              <div className="w-full md:w-auto flex-1">
                <input 
                  name="Username" 
                  autoComplete="off" 
                  value={fields.Username} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.Username ? 'border-red-400' : 'border-[#c8d8e4]'}`} 
                />
                {errors.Username && <span className="text-xs text-red-500 mt-1">{errors.Username}</span>}
              </div>
            </div>
            
            <div className='flex flex-col md:flex-row gap-2 md:justify-between md:items-center gap-x-2'>
              <label className="block md:w-1/4 text-sm font-medium text-[#2b6777] whitespace-nowrap">Full Name *</label>
              <div className="w-full md:w-auto flex-1">
                <input 
                  name="FullName" 
                  autoComplete="off" 
                  value={fields.FullName} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.FullName ? 'border-red-400' : 'border-[#c8d8e4]'}`} 
                />
                {errors.FullName && <span className="text-xs text-red-500 mt-1">{errors.FullName}</span>}
              </div>
            </div>
            
            <div className='flex flex-col md:flex-row gap-2 md:justify-between md:items-center gap-x-2'>
              <label className="block md:w-1/4 text-sm font-medium text-[#2b6777] whitespace-nowrap">Email *</label>
              <div className="w-full md:w-auto flex-1">
                <input 
                  name="Email" 
                  autoComplete="off" 
                  value={fields.Email} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.Email ? 'border-red-400' : 'border-[#c8d8e4]'}`} 
                />
                {errors.Email && <span className="text-xs text-red-500 mt-1">{errors.Email}</span>}
              </div>
            </div>
            
            <div className='flex flex-col md:flex-row gap-2 md:justify-between md:items-center gap-x-2'>
              <label className="block md:w-1/4 text-sm font-medium text-[#2b6777] whitespace-nowrap">Phone</label>
              <div className="w-full md:w-auto flex-1">
                <input 
                  name="Phone" 
                  autoComplete="off" 
                  value={fields.Phone} 
                  onChange={handleChange} 
                  className="w-full border border-[#c8d8e4] rounded px-3 py-2" 
                />
              </div>
            </div>
            
            <div className='flex flex-col md:flex-row gap-2 md:justify-between md:items-center gap-x-2'>
              <label className="block md:w-1/4 text-sm font-medium text-[#2b6777] whitespace-nowrap">Role *</label>
              <div className="w-full md:w-auto flex-1">
                <select 
                  name="Role" 
                  value={fields.Role} 
                  onChange={handleChange} 
                  className={`w-full border rounded px-3 py-2 ${errors.Role ? 'border-red-400' : 'border-[#c8d8e4]'}`}
                >
                  <option value="Superadmin">Super Admin</option>
                  <option value="Vendoradmin">Vendor Admin</option>
                  <option value="Vendoruser">Vendor User</option>
                </select>
                {errors.Role && <span className="text-xs text-red-500 mt-1">{errors.Role}</span>}
              </div>
            </div>
            
            <div className='flex flex-col md:flex-row gap-2 md:justify-between md:items-center gap-x-2'>
              <label className="block md:w-1/4 text-sm font-medium text-[#2b6777] whitespace-nowrap">Status *</label>
              <div className="w-full md:w-auto flex-1">
                <select 
                  name="IsActive" 
                  value={fields.IsActive} 
                  onChange={handleChange} 
                  className="w-full border rounded px-3 py-2 border-[#c8d8e4]"
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>
            
         <div className="flex flex-col md:flex-row gap-2 md:items-center md:col-span-2">
  <label className="block md:w-1/4 text-sm font-medium text-[#2b6777] whitespace-nowrap">
    Password {isEdit ? '' : '*'}
  </label>

  <div className="w-full flex-1">
    <div className="flex items-center border border-[#c8d8e4] rounded px-3 py-2 bg-white">
      <input
        name="Password"
        type={showPassword ? 'text' : 'password'}
        autoComplete="new-password"
        value={fields.Password}
        onChange={handleChange}
        placeholder={isEdit ? 'Leave blank to keep current password' : ''}
        className="flex-1 outline-none bg-transparent"
      />
      <button
        type="button"
        className="ml-2 text-[#2b6777]"
        onClick={() => setShowPassword(v => !v)}
        tabIndex={-1}
      >
        {showPassword ? <FaEyeSlash /> : <FaEye />}
      </button>
    </div>

    {errors.Password && (
      <span className="text-xs text-red-500 mt-1 block">
        {errors.Password}
      </span>
    )}
  </div>
</div>

          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-end gap-3 mt-4 md:mt-6">
          <button 
            type="button" 
            className="w-full sm:w-auto px-4 py-2 rounded bg-gray-200 text-[#2b6777] font-semibold order-2 sm:order-1" 
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="w-full sm:w-auto px-6 py-2 rounded bg-[#52ab98] hover:bg-[#2b6777] text-white font-semibold shadow order-1 sm:order-2" 
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEdit ? 'Update User' : 'Create User')}
          </button>
        </div>
      </form>
    </div>
  </div>
);
};

export default UserFormModal; 